/**
 * Shared color palette for the Admin Finance / P&L page (A7Y-2054).
 *
 * These are the single source of truth for both the recharts surfaces
 * (which need concrete hex/CSS color strings for `<Cell fill>` / `stroke`)
 * and the inline-styled legend dots in the tables. Keeping them as raw color
 * strings (not Tailwind classes) lets the same value drive a chart `fill` and
 * a `style={{ backgroundColor }}` swatch without duplication.
 *
 * Category hues are lifted from the `CATEGORY_PALETTE` in `SaasSpend.tsx`
 * (green / blue / purple / amber …) so the two finance surfaces read as one
 * visual system; the ledger's four categories map to the first four hues.
 */

/** Ledger spend categories → color. Keys match the "A7Y Ledger" Category column. */
export const CATEGORY_COLORS: Record<string, string> = {
  COGS: "#22c55e", // green-500
  "Business SaaS": "#3b82f6", // blue-500
  Infra: "#a855f7", // purple-500
  "Dev Tooling": "#f59e0b", // amber-500
};

/** Plan tiers → color. Keys match `PlanTier | 'other'`. */
export const TIER_COLORS: Record<string, string> = {
  free: "#71717a", // zinc-500
  basic: "#0ea5e9", // sky-500
  pro: "#6366f1", // indigo-500
  elite: "#8b5cf6", // violet-500
  enterprise: "#10b981", // emerald-500
  other: "#a1a1aa", // zinc-400
};

/**
 * Fallback hues for any category/tier not present in the maps above, so an
 * unexpected ledger label still gets a stable, distinct color rather than
 * collapsing to a single default.
 */
const FALLBACK_HUES = [
  "#f43f5e", // rose-500
  "#06b6d4", // cyan-500
  "#d946ef", // fuchsia-500
  "#10b981", // emerald-500
  "#f97316", // orange-500
  "#6366f1", // indigo-500
] as const;

/** Deterministic fallback color from an arbitrary key (stable across renders). */
function fallbackColor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return FALLBACK_HUES[Math.abs(hash) % FALLBACK_HUES.length];
}

/** Resolve a category color, falling back to a stable hue for unknown labels. */
export function categoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? fallbackColor(category);
}

/** Resolve a tier color, falling back to a stable hue for unknown tiers. */
export function tierColor(tier: string): string {
  return TIER_COLORS[tier] ?? fallbackColor(tier);
}
